import React from 'react';
import { BulkUploadResult, uploadFilesBulk } from '../services/api';

// UploadContext owns the *one* active bulk-upload session for the app.
// Hoisting it out of FileUploader means:
//   - The upload keeps running when the user navigates away from the page
//     that started it (the underlying promise didn't care about React
//     lifecycle, but its onProgress callback did — writing to context
//     from a stable ref keeps the state updates flowing).
//   - A FloatingUploadBar at app root can show progress anywhere.
//   - A returning FileUploader for the same collection can re-attach to
//     the in-flight session and show inline progress.
//
// Single-session scope on purpose: two concurrent bulk uploads for two
// different collections is a rare case, and one-at-a-time keeps the UX
// story (and this file) tractable.

export type UploadPhase = 'hashing' | 'uploading' | 'done' | 'error';

export type UploadProgress = {
  done: number;
  total: number;
  bytesUploaded: number;
  totalBytes: number;
  failed: number;
  skipped: number;
};

export type HashProgress = { hashed: number; total: number };

export type UploadSession = {
  // Bumps on every startUpload so effects that key on identity re-run.
  id: number;
  collectionId: string;
  // Total files/bytes at session start — the progress object also has
  // totals but they're only populated once uploading begins.
  totalFiles: number;
  totalBytes: number;
  // Path where the upload was initiated. FloatingUploadBar uses this
  // to hide itself when the user is back on the page that owns the
  // inline progress card.
  originPath: string;
  phase: UploadPhase;
  hashProgress: HashProgress | null;
  progress: UploadProgress | null;
  results: BulkUploadResult[] | null;
  error: string | null;
  startedAt: number;
};

type StartUploadInput = {
  collectionId: string;
  files: File[];
  token: string;
  concurrency?: number;
  originPath: string;
};

type UploadContextValue = {
  session: UploadSession | null;
  // Returns the assigned session id synchronously plus a promise for the
  // final results. Callers that need to distinguish "the session I
  // started" from "any session in flight" (e.g. FileUploader, so a stale
  // completed session from a previous page doesn't fire its callbacks)
  // capture the id before awaiting.
  startUpload: (opts: StartUploadInput) => { id: number; done: Promise<BulkUploadResult[]> };
  // Clears a completed/errored session so the UI resets.
  dismiss: () => void;
};

const Ctx = React.createContext<UploadContextValue | null>(null);

export const UploadProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = React.useState<UploadSession | null>(null);
  // sessionRef lets async callbacks read the current session id without
  // stale-closure headaches — they check `sessionRef.current?.id ===
  // localId` before writing progress, so a superseded upload can't
  // clobber a newer one.
  const sessionRef = React.useRef<UploadSession | null>(null);
  sessionRef.current = session;

  const nextId = React.useRef(0);

  const startUpload = React.useCallback((opts: StartUploadInput) => {
    const id = ++nextId.current;
    const totalBytes = opts.files.reduce((s, f) => s + f.size, 0);
    const initial: UploadSession = {
      id,
      collectionId: opts.collectionId,
      totalFiles: opts.files.length,
      totalBytes,
      originPath: opts.originPath,
      phase: 'hashing',
      hashProgress: { hashed: 0, total: opts.files.length },
      progress: null,
      results: null,
      error: null,
      startedAt: Date.now(),
    };
    setSession(initial);

    // patch() applies a partial update *only if* the session in state
    // still matches our id — protects against a supersede race where a
    // new upload started before this one finished emitting progress.
    const patch = (delta: Partial<UploadSession>) => {
      setSession((prev) => (prev && prev.id === id ? { ...prev, ...delta } : prev));
    };

    const done = (async () => {
      try {
        const results = await uploadFilesBulk({
          collectionId: opts.collectionId,
          files: opts.files,
          concurrency: opts.concurrency,
          token: opts.token,
          onHashProgress: (hp) => patch({ hashProgress: hp }),
          onProgress: (p) => patch({ phase: 'uploading', progress: p }),
        });
        patch({
          phase: results.some((r) => r.error) ? 'error' : 'done',
          results,
        });
        return results;
      } catch (e) {
        patch({ phase: 'error', error: (e as Error).message });
        throw e;
      }
    })();

    return { id, done };
  }, []);

  const dismiss = React.useCallback(() => {
    setSession(null);
  }, []);

  const value = React.useMemo(
    () => ({ session, startUpload, dismiss }),
    [session, startUpload, dismiss],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useUpload = (): UploadContextValue => {
  const v = React.useContext(Ctx);
  if (!v) throw new Error('useUpload must be used within <UploadProvider>');
  return v;
};

// True while a bulk upload is actively hashing or PUT-ing to S3.
// Callers use this to disable "New collection" / "New stack" entry
// points, so the one-upload-at-a-time invariant is visible in the UI
// (not just enforced at the data layer with a late error).
export const useUploadInFlight = (): boolean => {
  const { session } = useUpload();
  return session?.phase === 'hashing' || session?.phase === 'uploading';
};
